import type { ProviderDetection } from '../types/genetic';

/**
 * Identify a genetic data file's provider from its first chunk of bytes.
 *
 * Strategy: explicit provider markers in headers (high confidence), then
 * fall back to delimiter shape and column count (medium confidence).
 *   - 23andMe: comma-separated, 4 columns starting with rsid
 *   - Ancestry: tab-separated, 5 columns starting with rsid
 */
export const detectProvider = ({ firstChunk }: { firstChunk: string }): ProviderDetection => {
  const lowerChunk = firstChunk.toLowerCase();
  const lines = firstChunk.split('\n').slice(0, 20);

  if (lowerChunk.includes('23andme') || lowerChunk.includes('23andme.com')) {
    const buildMatch = firstChunk.match(/build\s*(3[67]|v[345])/i);
    return {
      provider: '23andMe',
      confidence: 'high',
      buildVersion: buildMatch ? buildMatch[1] : undefined,
    };
  }

  if (lowerChunk.includes('ancestrydna') || lowerChunk.includes('ancestry.com')) {
    return { provider: 'Ancestry', confidence: 'high' };
  }

  let commaCount = 0;
  let tabCount = 0;
  for (const line of lines) {
    if (line.startsWith('#') || line.trim() === '') continue;
    if ((line.match(/,/g) || []).length > 0) commaCount++;
    if ((line.match(/\t/g) || []).length > 0) tabCount++;
  }

  const dataLines = lines.filter((l) => !l.startsWith('#') && l.trim());

  if (commaCount > tabCount && dataLines.length > 0) {
    const parts = dataLines[0].split(',');
    if (parts.length === 4 && parts[0].startsWith('rs')) {
      return { provider: '23andMe', confidence: 'medium' };
    }
  }

  if (tabCount > commaCount && dataLines.length > 0) {
    const parts = dataLines[0].split('\t');
    if (parts.length === 5 && parts[0].startsWith('rs')) {
      return { provider: 'Ancestry', confidence: 'medium' };
    }
  }

  return { provider: 'unknown', confidence: 'low' };
};
